import * as React from "react";
import classnames from "classnames";

import Money from "web_modules/Money";
import ClickTo from "web_modules/shared/CollapseExpandToggle";
import { loanStatusToStr } from "web_modules/shared/loan_statuses";
import { loanDecisionToStr } from "web_modules/shared/loan_decisions";

import { EMPTY_PLACEHOLDER } from "./";
import { Queue } from "./types";
import {
  formatDateOrDatetime,
  getColorForCurrentCategory,
  QueueCategory,
  QueueColumnNames as Cols,
  getHeadingNameFromKey,
  uniqueIDFromQueueName,
} from "./shared";

const FontAwesome = require("react-fontawesome");
const styles = require("./styles.module.scss");

interface Props {
  rows: any;
  queue: Queue;
}

interface State {
  collapsed: boolean;
}

// Returns a styled + structured <td>
const wrappedCell = ({ children, tdStyles, uniqueKey, highlight }) => (
  <td key={uniqueKey} className={classnames(tdStyles, highlight)}>
    {children}
  </td>
);

// Returns a class string corresponding to 4 variants in the SCSS.
const getHighlightColor = (category: QueueCategory): string | null => {
  switch (category) {
    case QueueCategory.Prospect:
      return styles.hlProspect;
    case QueueCategory.PreApplications:
      return styles.hlPreapplications;
    case QueueCategory.RESPAComplete:
      return styles.hlRespaComplete;
    default:
      return null;
  }
};

export default class QueueTable extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      collapsed: false,
    };
    this.toggleCollapseTable = this.toggleCollapseTable.bind(this);
  }

  toggleCollapseTable() {
    this.setState(prevState => ({ collapsed: !prevState.collapsed }));
  }

  render() {
    const { queue, rows } = this.props;
    const { collapsed } = this.state;

    const { name, role, category, columns, highlightedColumns, openToPath } = queue;
    const accentColor = getColorForCurrentCategory(category);

    return (
      <div className={styles.queuesSection}>
        <h3
          id={uniqueIDFromQueueName(name)}
          onClick={this.toggleCollapseTable}
          className={styles.queuesHeading}
        >
          {category}: <span className={accentColor}>{name}</span> &nbsp;
          <ClickTo.Collapse collapseToggleFunc={this.toggleCollapseTable} isCollapsed={collapsed} />
        </h3>
        {!collapsed && queue && <p className={classnames(styles.role, accentColor)}>{role}</p>}
        {!collapsed && (
          <table
            className={classnames(
              styles.queuesTable,
              category === QueueCategory.PreApplications
                ? styles.queuesTablePreapplication
                : styles.queuesTableRespaComplete,
            )}
          >
            <thead>
              <tr>
                {queue &&
                  columns.map(k => {
                    // Rewrite column headings (e.g. 'loan_application_id' -> 'App #')
                    const headingName = getHeadingNameFromKey(k);
                    return (
                      <th
                        key={k}
                        className={classnames(
                          // Add style modifiers to highlight or widen certain elements
                          (k === Cols.PreAppUWDue || k === Cols.MessageResponseDue) &&
                            styles["-wide"],
                          highlightedColumns.includes(k) && getHighlightColor(category),
                        )}
                      >
                        {headingName}
                      </th>
                    );
                  })}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                // If the queue is empty, show an "all done" message
                <tr>
                  <td colSpan={columns.length} className={styles.queuesTableCellFullWidth}>
                    All done! <FontAwesome name="check" />
                  </td>
                </tr>
              )}

              {queue &&
                rows.map((app, rowIndex) => {
                  return (
                    <tr key={app.loan_application_id}>
                      {columns.map((colKey, colIndex) => {
                        const value = app[colKey];

                        // Helper func to cleanly generate <td>'s
                        const td = (children, tdStyles = styles.queuesTableCellDefault) =>
                          wrappedCell({
                            children,
                            tdStyles,
                            uniqueKey: `${rowIndex}_${colIndex}`,
                            highlight:
                              highlightedColumns.includes(colKey) && getHighlightColor(category),
                          });

                        // If it's an ID, make it a link to the loan app
                        if (colKey === Cols.LoanApplicationID) {
                          return td(
                            <a
                              className={styles.queuesLinkText}
                              href={`/underwriter/app/${app.loan_application_id}/${openToPath}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <span>{value}</span>
                              <FontAwesome
                                className={styles.queuesLinkIcon}
                                name="arrow-circle-right"
                              />
                            </a>,
                            styles.queuesTableCellLinked,
                          );
                        }

                        // Format currency values
                        if (
                          colKey === Cols.BorrowerEstimatedLoanAmount ||
                          colKey === Cols.BorrowerEstimatedPurchasePrice
                        ) {
                          return td(<Money value={Number(value)} decimals={false} />);
                        }

                        // Map loan status keys to their preset outputs
                        if (colKey === Cols.LoanStatus) {
                          return td(loanStatusToStr(value) || EMPTY_PLACEHOLDER);
                        }

                        if (colKey === Cols.LoanDecision) {
                          return td(loanDecisionToStr(value) || EMPTY_PLACEHOLDER);
                        }

                        // Format date & timestamps
                        if (
                          colKey === Cols.ApplicationDate ||
                          colKey === Cols.AppraisalReceivedDate ||
                          colKey === Cols.ClosingDate ||
                          colKey === Cols.ConditionResponseDue ||
                          colKey === Cols.CreditReportDate ||
                          colKey === Cols.LoanStatusDate ||
                          colKey === Cols.LoanDecisionDate ||
                          colKey === Cols.MessageResponseDue ||
                          colKey === Cols.NOIATimestamp ||
                          colKey === Cols.NOIAFollowup ||
                          colKey === Cols.NOIADue ||
                          colKey === Cols.PreAppAANDue ||
                          colKey === Cols.PreAppAANSent ||
                          colKey === Cols.PreAppUWDue ||
                          colKey === Cols.RegistrationDate ||
                          colKey === Cols.RegBCreditDecisionDue
                        ) {
                          return td(value ? formatDateOrDatetime(value) : EMPTY_PLACEHOLDER);
                        }

                        // Default
                        return td(value || EMPTY_PLACEHOLDER);
                      })}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        )}
        {collapsed && <ClickTo.Expand collapseToggleFunc={this.toggleCollapseTable} />}
      </div>
    );
  }
}
